import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Trash2, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import AriOrb from './AriOrb';
import { extractDestinations } from '../lib/ariDestinations';

const SUGGESTIONS = [
  'Where do I set up payroll giving?',
  'How do I match my team\u2019s donations?',
  'Show me how to customise our impact site',
];

const ChatbotWidget = ({ context = 'sales' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentView, setCurrentView] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const inApp = context === 'support';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Let anything on the page open Ari (e.g. the hero card)
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener('altrunic:open-chat', open);
    return () => window.removeEventListener('altrunic:open-chat', open);
  }, []);

  // the dashboard tells us which view the user is looking at, so Ari can
  // answer about the screen in front of them without being told
  useEffect(() => {
    const onView = (e) => setCurrentView(e.detail?.view || null);
    window.addEventListener('altrunic:view-change', onView);
    return () => window.removeEventListener('altrunic:view-change', onView);
  }, []);

  // Show greeting when chat opens for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = inApp
        ? "Hi, I'm Ari. Ask me anything about your account \u2014 campaigns, matching, payroll giving, your impact site. If it lives on a screen, I'll take you straight to it."
        : "Hi! I'm Ari from Altrunic. I can tell you all about how we help businesses make real social impact \u2014 for free. What would you like to know?";
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [isOpen, inApp, messages.length]);

  // Let the hero orb react while Ari is working on a reply
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('altrunic:ari-typing', { detail: { typing: loading } }));
  }, [loading]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/api/chatbot/conversation', {
        message: trimmed,
        session_id: sessionId,
        context,
        page: inApp ? currentView || window.location.pathname : null,
      });
      if (res.data.success) {
        setSessionId(res.data.session_id);
        const { content, destinations } = extractDestinations(res.data.response);
        setMessages((prev) => [...prev, { role: 'assistant', content, destinations }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting right now. Please try again in a moment, or reach out to support@altrunic.com."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const goTo = (dest) => {
    navigate(dest.view ? `${dest.path}?view=${dest.view}` : dest.path);
    window.dispatchEvent(new CustomEvent('altrunic:goto-view', { detail: { view: dest.view } }));
    setIsOpen(false);
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <>
      {/* Floating orb */}
      {!isOpen && (
        <button
          data-testid="chatbot-toggle-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Ask Ari"
          className="group fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-full pl-1.5 pr-4 py-1.5 alt-ari-launcher"
        >
          <AriOrb size={44} />
          <span className="text-sm font-semibold tracking-tight alt-ari-launcher-label">
            Ask Ari
          </span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] flex flex-col rounded-2xl overflow-hidden alt-ari-panel"
          style={{ height: '560px', maxHeight: 'calc(100vh - 6rem)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 alt-ari-panel-header">
            <div className="flex items-center gap-3">
              <AriOrb size={38} thinking={loading} />
              <div>
                <p className="font-semibold text-sm leading-tight alt-ari-title">Ari</p>
                <p className="text-xs alt-ari-subtitle">
                  {loading ? 'Thinking\u2026' : inApp ? 'Your impact guide' : 'Altrunic Assistant'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="chatbot-reset-btn"
                onClick={resetChat}
                className="p-1.5 rounded-lg transition-colors alt-ari-icon-btn"
                title="New conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                data-testid="chatbot-close-btn"
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg transition-colors alt-ari-icon-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 alt-ari-messages">
            {messages.map((msg, i) => (
              <div key={i} className="space-y-2 alt-ari-msg-in">
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    data-testid={`chat-message-${msg.role}-${i}`}
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                      msg.role === 'user'
                        ? 'rounded-br-md alt-ari-bubble-user'
                        : 'rounded-bl-md alt-ari-bubble-ari'
                    }`}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {msg.content}
                  </div>
                </div>

                {/* "Take me there" - one tap to the exact screen */}
                {msg.destinations?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-1">
                    {msg.destinations.map((dest) => (
                      <button
                        key={dest.key}
                        data-testid={`ari-goto-${dest.key}`}
                        onClick={() => goTo(dest)}
                        className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold alt-ari-goto"
                      >
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                        Take me to {dest.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* starter prompts, in-app only, before the first question */}
            {inApp && messages.length === 1 && !loading && (
              <div className="pt-1 space-y-2">
                <p className="text-xs flex items-center gap-1.5 alt-ari-subtitle">
                  <Sparkles className="w-3 h-3" /> Try asking
                </p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`ari-suggestion-${s.slice(0, 12).toLowerCase().replace(/\W+/g, '-')}`}
                    onClick={() => send(s)}
                    className="block w-full text-left text-xs px-3 py-2 rounded-xl alt-ari-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-2.5 rounded-2xl rounded-bl-md flex items-center gap-2 text-sm alt-ari-bubble-ari">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Ari is typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 alt-ari-composer">
            <div className="flex items-center gap-2 rounded-xl px-3 py-1.5 alt-ari-input-wrap">
              <input
                ref={inputRef}
                data-testid="chatbot-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inApp ? 'Ask Ari anything\u2026' : 'Type a message...'}
                className="flex-1 bg-transparent text-sm outline-none py-1.5 alt-ari-input"
                disabled={loading}
              />
              <button
                data-testid="chatbot-send-btn"
                onClick={() => send()}
                disabled={!input.trim() || loading}
                className="p-1.5 rounded-lg transition-all disabled:opacity-30 alt-ari-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-center text-[10px] mt-1.5 alt-ari-subtitle">Powered by Altrunic AI</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
